import { fetchJson, mutate, qs } from './base-roros.service';

// ─── Skills ─────────────────────────────────────────────────────────────────

export interface RorosSkillDTO {
  id: string;
  orgId: string;
  name: string;
  description: string | null;
  category: string | null;
  assignments: { agentDefinitionId: string; proficiency: string }[];
  createdAt: string;
  updatedAt: string;
}

export interface RorosSkillMatrixDTO {
  skills: RorosSkillDTO[];
  agents: { id: string; name: string }[];
  matrix: Record<string, Record<string, string>>;
}

export const RorosSkillService = {
  list: (params?: Record<string, string>) =>
    fetchJson<RorosSkillDTO[]>(`/api/v1/agent-skills${qs(params)}`),
  getMatrix: () =>
    fetchJson<RorosSkillMatrixDTO>('/api/v1/agent-skills/matrix'),
  create: (payload: Record<string, unknown>) =>
    mutate<RorosSkillDTO>('/api/v1/agent-skills', 'POST', payload),
  get: (id: string) =>
    fetchJson<RorosSkillDTO>(`/api/v1/agent-skills/${id}`),
  update: (id: string, payload: Record<string, unknown>) =>
    mutate<RorosSkillDTO>(`/api/v1/agent-skills/${id}`, 'PUT', payload),
  remove: (id: string) =>
    mutate(`/api/v1/agent-skills/${id}`, 'DELETE'),
  assign: (id: string, payload: { agent_definition_id: string; proficiency: string }) =>
    mutate(`/api/v1/agent-skills/${id}/assign`, 'POST', payload),
  unassign: (id: string, agentDefinitionId: string) =>
    mutate(`/api/v1/agent-skills/${id}/unassign/${agentDefinitionId}`, 'DELETE'),
};

// ─── Agent Definitions ──────────────────────────────────────────────────────

/**
 * Delegation config stored in `roros_agent_definitions.delegation_config` (jsonb).
 * J7-W1 added the moderator flags — no schema change needed (jsonb).
 */
export interface RorosDelegationConfig {
  can_delegate?: boolean;
  max_parallel?: number;
  allowed_delegate_actors?: string[];
  /** PRD §9.7.2 — when true AND adoption_level !== 'shadow', this operator
   *  is the tenant's Decision Moderator. */
  is_decision_moderator?: boolean;
  /** M-Rhythm-1 — when true, this operator is the tenant's Meeting Moderator. */
  is_meeting_moderator?: boolean;
  moderator_domain_scope?: string[];
  moderator_trigger_rules?: {
    auto_invoke_on?: Array<'root' | 'trunk' | 'branch' | 'leaf'>;
  };
}

/**
 * Matches the shape returned by `toAgentDefinitionDTO()` on the server
 * (`server/modules/ai-operators/ai-operators.types.ts`). The earlier shape
 * in this file was placeholder — J7-W1 brings it in line with the API.
 */
export interface RorosAgentDefinitionDTO {
  id: string;
  orgId: string;
  actorId: string;
  icon: string | null;
  defaultModelTier: string;
  instructions: string | null;
  capabilities: any[];
  delegationConfig: RorosDelegationConfig;
  provider: string | null;
  isSystem: boolean;
  systemVersion: number | null;
  clonedFromId: string | null;
  /** AGS-M2 — per-actor tool allowlist. */
  allowedTools: string[];
  /** AC-AGS-M4-1 — routing fields */
  routingDescription: string | null;
  isCoordinator: boolean;
  createdAt: string;
  updatedAt: string;
}

export const RorosAgentService = {
  list: () =>
    fetchJson<RorosAgentDefinitionDTO[]>('/api/v1/agent-definitions'),
  create: (payload: Record<string, unknown>) =>
    mutate<RorosAgentDefinitionDTO>('/api/v1/agent-definitions', 'POST', payload),
  clone: (payload: Record<string, unknown>) =>
    mutate<RorosAgentDefinitionDTO>('/api/v1/agent-definitions/clone', 'POST', payload),
  execute: (payload: Record<string, unknown>) =>
    mutate<Record<string, unknown>>('/api/v1/agent-definitions/execute', 'POST', payload),
  get: (id: string) =>
    fetchJson<RorosAgentDefinitionDTO>(`/api/v1/agent-definitions/${id}`),
  update: (id: string, payload: Record<string, unknown>) =>
    mutate<RorosAgentDefinitionDTO>(`/api/v1/agent-definitions/${id}`, 'PUT', payload),
  remove: (id: string) =>
    mutate(`/api/v1/agent-definitions/${id}`, 'DELETE'),
};

// ─── Agent Memory ───────────────────────────────────────────────────────────

export interface RorosAgentMemoryDTO {
  id: string;
  orgId: string;
  agentDefinitionId: string | null;
  memoryType: string;
  key: string;
  content: string;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export const RorosAgentMemoryService = {
  list: (params?: Record<string, string>) =>
    fetchJson<RorosAgentMemoryDTO[]>(`/api/v1/agent-memory${qs(params)}`),
  create: (payload: Record<string, unknown>) =>
    mutate<RorosAgentMemoryDTO>('/api/v1/agent-memory', 'POST', payload),
  get: (id: string) =>
    fetchJson<RorosAgentMemoryDTO>(`/api/v1/agent-memory/${id}`),
  update: (id: string, payload: Record<string, unknown>) =>
    mutate<RorosAgentMemoryDTO>(`/api/v1/agent-memory/${id}`, 'PUT', payload),
  remove: (id: string) =>
    mutate(`/api/v1/agent-memory/${id}`, 'DELETE'),
};

// ─── Task Delegations ───────────────────────────────────────────────────────

export interface RorosTaskDelegationDTO {
  id: string;
  orgId: string;
  taskDescription: string;
  delegatorActorId: string;
  delegateAgentDefinitionId: string;
  workItemId: string | null;
  status: string;
  result: Record<string, unknown> | null;
  cancelledAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export const RorosTaskDelegationService = {
  list: (params?: Record<string, string>) =>
    fetchJson<RorosTaskDelegationDTO[]>(`/api/v1/task-delegations${qs(params)}`),
  create: (payload: Record<string, unknown>) =>
    mutate<RorosTaskDelegationDTO>('/api/v1/task-delegations', 'POST', payload),
  get: (id: string) =>
    fetchJson<RorosTaskDelegationDTO>(`/api/v1/task-delegations/${id}`),
  updateStatus: (id: string, payload: Record<string, unknown>) =>
    mutate<RorosTaskDelegationDTO>(`/api/v1/task-delegations/${id}/status`, 'PUT', payload),
  cancel: (id: string) =>
    mutate<RorosTaskDelegationDTO>(`/api/v1/task-delegations/${id}/cancel`, 'POST'),
};
